import { useCallback, useEffect, useRef, useState } from "react";
import { DEFAULT_MAX_SOUND_DURATION_MS } from "@spieleparadies/shared";

export interface RecordingResult {
  dataUrl: string;
  mime: string;
  durationMs: number;
  blob: Blob;
}

interface SoundRecorderState {
  recording: boolean;
  elapsedMs: number;
  error: string | null;
  /** Ist die Mikro-API verfügbar (HTTPS / localhost)? */
  available: boolean;
}

/**
 * Kapselt MediaRecorder: Aufnahme starten, automatisch nach `MAX_SOUND_DURATION_MS`
 * stoppen, Ergebnis als Data-URL liefern. Macht außerdem die typischen
 * Browser-Fehler (kein HTTPS, keine Mikro-Berechtigung) zugänglich.
 */
export function useSoundRecorder() {
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const startedAtRef = useRef<number>(0);
  const stopResolveRef = useRef<((result: RecordingResult | null) => void) | null>(null);
  const elapsedTimerRef = useRef<number | null>(null);
  const autoStopTimerRef = useRef<number | null>(null);

  const [state, setState] = useState<SoundRecorderState>({
    recording: false,
    elapsedMs: 0,
    error: null,
    available:
      typeof navigator !== "undefined" &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function" &&
      typeof MediaRecorder !== "undefined",
  });

  const cleanup = useCallback(() => {
    if (elapsedTimerRef.current !== null) {
      window.clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    if (autoStopTimerRef.current !== null) {
      window.clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const start = useCallback(async (
    options: { maxDurationMs?: number } = {},
  ): Promise<RecordingResult | null> => {
    const maxDurationMs = options.maxDurationMs ?? DEFAULT_MAX_SOUND_DURATION_MS;
    if (state.recording) return null;
    if (!state.available) {
      setState((s) => ({
        ...s,
        error: "Mikro-API ist nicht verfügbar. Bitte HTTPS oder localhost benutzen.",
      }));
      return null;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      // Format aushandeln – wir bevorzugen webm/opus.
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg",
      ];
      const mime =
        candidates.find((c) =>
          typeof MediaRecorder.isTypeSupported === "function"
            ? MediaRecorder.isTypeSupported(c)
            : false,
        ) ?? "";
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];
      startedAtRef.current = performance.now();

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      const finished = new Promise<RecordingResult | null>((resolve) => {
        stopResolveRef.current = resolve;
      });

      recorder.onstop = async () => {
        const durationMs = Math.max(0, Math.floor(performance.now() - startedAtRef.current));
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mime || "audio/webm" });
        cleanup();
        setState((s) => ({ ...s, recording: false, elapsedMs: durationMs }));
        if (blob.size === 0) {
          stopResolveRef.current?.(null);
          stopResolveRef.current = null;
          return;
        }
        const dataUrl = await blobToDataUrl(blob);
        stopResolveRef.current?.({
          dataUrl,
          mime: blob.type,
          durationMs,
          blob,
        });
        stopResolveRef.current = null;
      };

      recorder.start();
      setState({ recording: true, elapsedMs: 0, error: null, available: state.available });

      elapsedTimerRef.current = window.setInterval(() => {
        setState((s) => ({ ...s, elapsedMs: performance.now() - startedAtRef.current }));
      }, 100);

      // Hartes Auto-Stop, damit die Aufnahme nicht zu lang wird.
      autoStopTimerRef.current = window.setTimeout(() => {
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
          recorderRef.current.stop();
        }
      }, maxDurationMs);

      return finished;
    } catch (err) {
      cleanup();
      let message = "Mikro konnte nicht gestartet werden.";
      if (err instanceof DOMException) {
        if (err.name === "NotAllowedError" || err.name === "SecurityError") {
          message = "Mikro-Zugriff verweigert. Bitte erlauben und neu laden.";
        } else if (err.name === "NotFoundError") {
          message = "Kein Mikrofon gefunden.";
        } else if (err.message) {
          message = err.message;
        }
      } else if (err instanceof Error && err.message) {
        message = err.message;
      }
      setState((s) => ({ ...s, recording: false, error: message }));
      return null;
    }
  }, [state.recording, state.available, cleanup]);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  }, []);

  return { ...state, start, stop };
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(blob);
  });
}
